import { TOOL_OPTIONS } from "@/constants/tool";
import { isWorkerDashboardPath } from "@/constants";
import { MenuItem, Tool } from "@/types";
import env from "@/env";
import { explorerPrefixedPath } from "@/utils";
import localCookies from "@/utils/cookie";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

type UseMainStoreProps = {
  tool: Tool;
  setTool: (tool: Tool) => void;
  getToolOptions: () => Tool[];
  updateToolToLocation: () => void;
  /** Desktop: compact icon-only rail (`true`). Does not hide the sidebar — mobile sheet ignores this (always expanded). */
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Desktop app shell content width mode: constrained (`max 1440px`) vs full width. */
  isAppContentFullWidth: boolean;
  toggleAppContentWidthMode: () => void;
  /** Left nav drawer on viewports below `md` (Sheet); not persisted. */
  mobileSidebarSheetOpen: boolean;
  setMobileSidebarSheetOpen: (open: boolean) => void;
  toggleMobileSidebarSheet: () => void;
};

export const useMainStore = create(
  persist<UseMainStoreProps>(
    (set, get) => ({
      tool: TOOL_OPTIONS[0],
      setTool: async (tool: Tool) => {
        set({ tool });
      },
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      isAppContentFullWidth: false,
      toggleAppContentWidthMode: () =>
        set((state) => ({ isAppContentFullWidth: !state.isAppContentFullWidth })),
      mobileSidebarSheetOpen: false,
      setMobileSidebarSheetOpen: (open) => set({ mobileSidebarSheetOpen: open }),
      toggleMobileSidebarSheet: () =>
        set((state) => ({ mobileSidebarSheetOpen: !state.mobileSidebarSheetOpen })),
      getToolOptions: () => {
        let options = TOOL_OPTIONS;

        if (env.EXPLORER_COMBINED) {
          const toRemove = new Set(["worker", "id", "staking"]);
          const explorer = options.find((t) => t.value === "explorer");
          const worker = options.find((t) => t.value === "worker");
          const id = options.find((t) => t.value === "id");
          const staking = options.find((t) => t.value === "staking");

          const prefixHref = (items: MenuItem[], prefix: string): MenuItem[] =>
            items.map((item) => ({
              ...item,
              href: item.href ? `${prefix}${item.href}` : item.href
            }));

          const pathname = typeof window !== "undefined" ? window.location.pathname : "";
          const isStakingPath = pathname.startsWith("/explorer/staking");
          const isIdPath =
            pathname.startsWith("/explorer/id") || pathname.startsWith("/cloud/id");

          let explorerMenuItems: MenuItem[];
          if (isIdPath) {
            explorerMenuItems = (id?.menuItems ?? []).map((item) => ({
              ...item,
              href: item.href ? explorerPrefixedPath(item.href) : item.href
            }));
          } else if (isWorkerDashboardPath(pathname)) {
            explorerMenuItems = worker?.menuItems ?? [];
          } else if (isStakingPath) {
            explorerMenuItems = prefixHref(staking?.menuItems ?? [], "/explorer");
          } else {
            explorerMenuItems = [...(explorer?.menuItems ?? [])];
          }

          options = options
            .filter((t) => !toRemove.has(t.value))
            .map((t) => (t.value === "explorer" ? { ...t, menuItems: explorerMenuItems } : t));
        }

        return options;
      },
      updateToolToLocation: () => {
        const { getToolOptions, setTool } = get();
        const pathname = window.location.pathname;

        if (isWorkerDashboardPath(pathname)) {
          if (env.EXPLORER_COMBINED) {
            const explorerTool = getToolOptions().find(({ value }) => value === "explorer");
            if (explorerTool) {
              setTool(explorerTool);
            }
          } else {
            const workerTool = getToolOptions().find(({ value }) => value === "worker");
            if (workerTool) {
              setTool(workerTool);
            }
          }
          return;
        }

        if (
          pathname.startsWith("/cloud/id") ||
          pathname.startsWith("/explorer/id") ||
          pathname.startsWith("/worker/devices/explorer") ||
          pathname.startsWith("/explorer/staking") ||
          pathname.startsWith("/explorer/")
        ) {
          const explorerTool = getToolOptions().find(({ value }) => value === "explorer");
          if (explorerTool) {
            setTool(explorerTool);
          }
          return;
        }

        const toolOption = getToolOptions().find(({ href }) => {
          const section = href?.split("/")[1];
          const currentSection = window.location.pathname.split("/")[1];

          if (currentSection === "login" && section === "id") {
            return true;
          }
          return section === currentSection;
        });

        if (toolOption) {
          setTool(toolOption);
        }
      }
    }),
    {
      name: "main",
      partialize: (state) => ({
        tool: state.tool,
        sidebarCollapsed: state.sidebarCollapsed,
        isAppContentFullWidth: state.isAppContentFullWidth
      }),
      storage: createJSONStorage(() => localCookies),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.log("an error happened during hydration", error);
          } else if (state) {
            state.updateToolToLocation();
          }
        };
      }
    }
  )
);
